import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Avatar,
    Grid,
    Card,
    CardContent,
} from '@mui/material'
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Devices as DevicesIcon,
    Business as BusinessIcon,
    Refresh as RefreshIcon,
    QrCode as QrCodeIcon,
    Download as DownloadIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { usePagination, useLoading } from '../../hooks'
import { StatusChip } from '../common/Chips'
import { deviceApi } from '../../api/deviceApi'
import { organizationApi } from '../../api/organizationApi'
import TableSkeleton from '../common/Skeleton/TableSkeleton'
import { exportToCSV } from '../../utils/exportUtils'
import { formatDate } from '../../utils/formatters'

const DeviceManager = ({ organizationId }) => {
    const navigate = useNavigate()
    const [searchTerm, setSearchTerm] = useState('')
    const { loading, startLoading, stopLoading } = useLoading()
    const pagination = usePagination(10)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedDevice, setSelectedDevice] = useState(null)
    const [filterStatus, setFilterStatus] = useState('all')
    const [filterOrganization, setFilterOrganization] = useState('all')

    const [organizations, setOrganizations] = useState([])
    const [devices, setDevices] = useState([])
    const [totalDevices, setTotalDevices] = useState(0)
    const [filters, setFilters] = useState({})

    const [formData, setFormData] = useState({
        name: '',
        deviceKey: '',
        organization: '',
        location: '',
        version: 'v1.0.0'
    })


    const fetchDevices = useCallback(async () => {
        startLoading()
        try {
            const queryParams = {
                page: pagination.page + 1,
                limit: pagination.rowsPerPage,
                ...filters
            }

            const response = await deviceApi.listDevices(queryParams)
            let fetchedDevices = response.data.devices || []
            let total = response.data.total || 0

            if (organizationId) {
                fetchedDevices = fetchedDevices.filter(d =>
                    d.org_id === parseInt(organizationId) || d.organization_id === parseInt(organizationId)
                )
                total = fetchedDevices.length
            }

            setDevices(fetchedDevices)
            setTotalDevices(total)
        } catch (error) {
            console.error("Failed to fetch devices", error)
            toast.error("Failed to fetch devices")
        } finally {
            stopLoading()
        }
    }, [pagination.page, pagination.rowsPerPage, filters, organizationId])


    useEffect(() => {
        const loadData = async () => {
            if (!organizationId) {
                try {
                    const response = await organizationApi.listOrganizations()
                    setOrganizations(response.data || [])
                } catch (error) {
                    console.error("Failed to fetch organizations", error)
                    toast.error("Failed to fetch organizations")
                }
            }
            fetchDevices()
        }
        loadData()
    }, [organizationId, fetchDevices])




    const handleAddDevice = () => {
        setSelectedDevice(null)
        setFormData({
            name: '',
            deviceKey: '',
            organization: organizationId ? parseInt(organizationId) : '',
            location: '',
            version: 'v1.0.0'
        })
        setDialogOpen(true)
    }

    const handleEditDevice = async (device) => {
        setSelectedDevice(device)
        setFormData({
            name: device.name,
            deviceKey: device.device_key,
            organization: device.org_id,
            location: device.location,
            version: device.version
        })
        setDialogOpen(true)
    }

    const handleDeleteDevice = async (device) => {
        if (window.confirm(`Delete device ${device.name}?`)) {
            try {
                const response = await deviceApi.deleteDevice(device.id, { skipSuccessToast: true })
                toast.success(response.data.detail || 'Device deleted successfully!')
                fetchDevices()
            } catch (error) {
                console.error("Delete failed", error)
                toast.error('Failed to delete device')
            }
        }
    }

    const handleSaveDevice = async (e) => {
        e.preventDefault()

        // Location Validation
        const locationRegex = /^[a-zA-Z0-9\s,.\-#/()]+$/
        if (!locationRegex.test(formData.location)) {
            toast.error('Location contains invalid characters. Allowed: Letters, numbers, spaces, and , . - # / ( )')
            return
        }

        const payload = {
            name: formData.name,
            device_key: formData.deviceKey,
            org_id: formData.organization,
            location: formData.location,
            version: formData.version,
            status: 'active'
        }

        try {
            if (selectedDevice) {
                await deviceApi.updateDevice(selectedDevice.id, payload, { skipSuccessToast: true })
                toast.success('Device updated successfully')
            } else {
                const response = await deviceApi.registerDevice(payload, { skipSuccessToast: true })
                toast.success(response.data.message || 'Device registered successfully')
            }
            setDialogOpen(false)
            fetchDevices()
        } catch (error) {
            console.error("Save failed", error)
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to save device'
            toast.error(errorMessage)
        }
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    // Frontend Filtering for Search/Status
    const filteredDevices = devices.filter(device => {
        const matchesSearch = (device.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (device.device_key || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (device.location || '').toLowerCase().includes(searchTerm.toLowerCase())

        const matchesStatus = filterStatus === 'all' || device.status === filterStatus
        const matchesOrg = filterOrganization === 'all' || device.org_name === filterOrganization

        return matchesSearch && matchesStatus && matchesOrg
    })

    // Pagination for display
    const paginatedDevices = filteredDevices.slice(
        pagination.page * pagination.rowsPerPage,
        pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
    )

    const stats = {
        totalDevices: devices.length,
        activeDevices: devices.filter(d => d.status === 'active').length,
        inactiveDevices: devices.filter(d => d.status === 'inactive').length,
    }

    const handleExport = () => {
        const columns = [
            { label: 'Name', key: 'name' },
            { label: 'Device Key', key: 'device_key' },
            { label: 'Organization', key: 'org_name' },
            { label: 'Location', key: 'location' },
            { label: 'Version', key: 'version' },
            { label: 'Status', key: 'status' },
            { label: 'Created At', key: (row) => formatDate(row.created_at) }
        ]
        exportToCSV(filteredDevices, columns, 'devices-list')
    }

    return (
        <Box sx={{ p: organizationId ? 0 : { xs: 1, md: 3 } }}>
            {/* Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus>
                <DialogTitle>
                    {selectedDevice ? 'Edit Device' : 'Register New Device'}
                </DialogTitle>
                <form onSubmit={handleSaveDevice}>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Device Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Device Key"
                                value={formData.deviceKey}
                                onChange={(e) => handleInputChange('deviceKey', e.target.value)}
                                margin="normal"
                                helperText="Unique identifier for the device"
                                required
                            />
                            {!organizationId && (
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel>Organization</InputLabel>
                                    <Select
                                        label="Organization"
                                        value={formData.organization}
                                        onChange={(e) => handleInputChange('organization', e.target.value)}
                                        required
                                    >
                                        {organizations.map((org) => (
                                            <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <TextField
                                fullWidth
                                label="Location"
                                value={formData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Version"
                                value={formData.version}
                                onChange={(e) => handleInputChange('version', e.target.value)}
                                margin="normal"
                                required
                            />

                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">
                            {selectedDevice ? 'Update' : 'Register'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Box sx={{ mb: 4 }}>



                {!organizationId && (
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                                            <DevicesIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h4" fontWeight="bold">
                                                {stats.totalDevices}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Total Devices
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                                            <CheckCircleIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h4" fontWeight="bold">
                                                {stats.activeDevices}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Active
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#dc004e', mr: 2 }}>
                                            <CancelIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h4" fontWeight="bold">
                                                {stats.inactiveDevices}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Inactive
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Filters and Search */}
                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search devices..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ flexGrow: 1, width: { xs: '100%', md: 'auto' } }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', md: 'auto' } }}>
                            <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Only show Org filter if NOT in Org Admin mode */}
                            {!organizationId && (
                                <FormControl size="small" sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}>
                                    <InputLabel>Organization</InputLabel>
                                    <Select
                                        label="Organization"
                                        value={filterOrganization}
                                        onChange={(e) => setFilterOrganization(e.target.value)}
                                    >
                                        <MenuItem value="all">All Organizations</MenuItem>
                                        {organizations.map((org) => (
                                            <MenuItem key={org.id} value={org.name}>{org.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>

                        <Button
                            variant="outlined"
                            onClick={fetchDevices}
                            disabled={loading}
                            startIcon={<RefreshIcon />}
                            size="small"
                            sx={{ width: { xs: '100%', md: 'auto' } }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Devices Table */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        sx={{ flex: { xs: 1, md: 'none' } }}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddDevice}
                        sx={{ flex: { xs: 1, md: 'none' } }}
                    >
                        Register Device
                    </Button>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Device</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell>Updated At</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableSkeleton columns={7} rows={5} />
                            ) : paginatedDevices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1" color="textSecondary">
                                            No devices found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedDevices.map((device) => (
                                    <TableRow key={device.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography fontWeight="medium">{device.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {device.device_key} • v{device.version}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <BusinessIcon fontSize="small" color="action" />
                                                <Typography variant="body2">{device.org_name || 'N/A'}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{device.location}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={device.status} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(device.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(device.updated_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditDevice(device)}
                                                title="Edit"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                title="View QR"
                                            >
                                                <QrCodeIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteDevice(device)}
                                                title="Delete"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredDevices.length}
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page}
                    onPageChange={pagination.handleChangePage}
                    onRowsPerPageChange={pagination.handleChangeRowsPerPage}
                />
            </Paper>
        </Box >
    )
}

export default DeviceManager
